import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../../../lib/api';
import { formatErr } from '../../../../components/admin/billing/shared';

const KINDS = [['customers', 'Customers + vehicles'], ['balances', 'Open balances'], ['stock', 'Stock / parts']];
const summary = (k, r) => (k === 'customers' ? `${r.customers_created} new, ${r.customers_merged} merged, ${r.vehicles_added} vehicles`
  : k === 'balances' ? `₹ ${Number(r.total_due).toLocaleString('en-IN')} across ${r.customers_with_dues} customers`
    : `${r.parts_created} new, ${r.parts_updated} updated parts`);

// Team-only: upload a sheet, match its columns to Driveline fields, preview, import into the linked workshop.
export const ImportTool = ({ mid, linked, authHeader, onDone }) => {
  const [kind, setKind] = useState('customers');
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [map, setMap] = useState({});
  const [cutoff, setCutoff] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);

  const form = (extra = {}) => {
    const b = new FormData();
    b.append('kind', kind); b.append('file', file);
    Object.entries(extra).forEach(([k, v]) => b.append(k, v));
    return b;
  };
  const preview = async (f = file, k = kind) => {
    if (!f) return;
    setBusy('preview'); setResult(null);
    try {
      const b = new FormData(); b.append('kind', k); b.append('file', f);
      const { data } = await axios.post(`${API}/platform/migrations/${mid}/import/preview`, b, { headers: authHeader() });
      setPrev(data); setMap(data.mapping);
    } catch (e) { setPrev(null); toast.error(formatErr(e)); } finally { setBusy(''); }
  };
  const run = async () => {
    setBusy('run');
    try {
      const { data } = await axios.post(`${API}/platform/migrations/${mid}/import`, form({ mapping: JSON.stringify(map), cutoff }), { headers: authHeader() });
      setResult(data); toast.success(`Imported: ${summary(kind, data)}`); onDone();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(''); }
  };
  const cols = prev ? Object.values(map).filter(Boolean) : [];

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="mig-import">
      <h3 className="text-sm font-semibold text-slate-900">Import tool</h3>
      {!linked && <p className="text-xs text-amber-700" data-testid="mig-import-unlinked">Link this request to a workshop account before importing.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <select value={kind} onChange={(e) => { setKind(e.target.value); setPrev(null); if (file) preview(file, e.target.value); }} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" data-testid="mig-import-kind">
          {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input type="file" accept=".csv,.xlsx" onChange={(e) => { const f = e.target.files?.[0]; setFile(f); preview(f); }} className="text-sm" data-testid="mig-import-file" />
        {kind === 'balances' && <label className="text-xs text-slate-600">Cut-off <input type="date" value={cutoff} onChange={(e) => setCutoff(e.target.value)} className="ml-1 rounded border border-slate-300 px-1.5 py-1" data-testid="mig-import-cutoff" /></label>}
        {busy === 'preview' && <Loader2 size={14} className="animate-spin" />}
      </div>
      {prev && (
        <>
          <div className="grid gap-2 sm:grid-cols-3" data-testid="mig-import-mapping">
            {prev.fields.map((f) => (
              <label key={f.key} className="text-xs text-slate-600">{f.label}{f.required && ' *'}
                <select value={map[f.key] || ''} onChange={(e) => setMap((m) => ({ ...m, [f.key]: e.target.value }))} className="mt-0.5 block w-full rounded border border-slate-300 px-1.5 py-1 text-sm" data-testid={`mig-map-${f.key}`}>
                  <option value="">Not in file</option>{prev.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select></label>
            ))}
          </div>
          <div className="max-h-64 overflow-auto rounded border border-slate-200" data-testid="mig-import-preview">
            <table className="w-full text-xs"><thead className="sticky top-0 bg-slate-50"><tr>{prev.fields.filter((f) => map[f.key]).map((f) => <th key={f.key} className="px-2 py-1 text-left">{f.label}</th>)}</tr></thead>
              <tbody>{prev.rows.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i} className="border-t border-slate-100">{prev.fields.filter((f) => map[f.key]).map((f) => <td key={f.key} className={`px-2 py-1 ${f.required && !r[map[f.key]] ? 'bg-amber-50' : ''}`}>{r[map[f.key]]}</td>)}</tr>))}</tbody></table>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">First {prev.rows.length} of {prev.total} rows · {cols.length} columns matched. Same phone or registration is merged, not doubled.</span>
            <button type="button" onClick={run} disabled={!linked || !!busy} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="mig-import-run">
              {busy === 'run' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import {prev.total} rows</button>
          </div>
        </>
      )}
      {result && <p className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="mig-import-result">{summary(kind, result)}{result.issue_count ? ` · ${result.issue_count} row(s) flagged` : ''}</p>}
    </div>
  );
};
